import {Component, Inject, OnInit, Optional} from '@angular/core';
import {
  FormGroup,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {IDropdownObject} from 'src/app/interfaces/dropdown-object.interface';
import {
  IHrDataDropdowns,
  IOneToOneModal,
  IOneToOneTopics,
} from 'src/app/interfaces/hr-data.interface';
import {HrDataService} from 'src/app/services/employee-details/hr-data.service';
import {Utils} from 'src/app/shared/utils/utils';
import {DIALOG_DATA} from '../../../../services/dialog.service';
import {DialogRef} from '../../../../shared/ui-kits/dialog/dialog-ref';

@UntilDestroy()
@Component({
  selector: 'app-one-to-one',
  templateUrl: './one-to-one.component.html',
  styleUrls: ['./one-to-one.component.scss'],
})
export class OneToOneComponent implements OnInit {
  oneToOneForm!: UntypedFormGroup;
  dataFromOneToOne!: IOneToOneModal;
  typeDropdown!: IDropdownObject[];
  riskLevelDropDown!: IDropdownObject[];
  knowledgeDropDown!: IDropdownObject[];
  public hrDropdown!: IDropdownObject[];
  public topicDropdown!: IDropdownObject[];
  public statusDropdown!: IDropdownObject[];
  public importanceLevelDropDown!: IDropdownObject[];
  public satisfactionDropDown!: IDropdownObject[];
  public isFieldInvalid = Utils.isFieldInvalid;
  public validateAllFormFields = Utils.validateAllFormFields;
  public isDatePickerOpened!: boolean;
  readonly MAX_TOPICS_COUNT = 6;

  constructor(
    private dialogRef: DialogRef,
    @Optional() @Inject(DIALOG_DATA) public data: IOneToOneModal,
    private _fb: UntypedFormBuilder,
    private hrDataService: HrDataService
  ) {}

  ngOnInit() {
    this.dataFromOneToOne = this.data;
    this.oneToOneForm = this.buildForm();
    this.setDropdownsValue(this.data.dropdowns);

    if (this.data.value.oneToOneTopics.length) {
      this.topicsArray.clear();

      this.data.value.oneToOneTopics.forEach(topic =>
        this.createOneToOneTopicGroup(topic)
      );
    }

    this.subscribeOnFormChanges();
  }

  setDropdownsValue(res: IHrDataDropdowns) {
    this.typeDropdown = res.OneToOneType;
    this.riskLevelDropDown = res.RiskLevel;
    this.knowledgeDropDown = res.QualityLevel;
    this.topicDropdown = res.Topic;
    this.statusDropdown = res.OneToOneTopicStatusType;
    this.importanceLevelDropDown = res.ImportanceLevel;
    this.satisfactionDropDown = res.SatisfactionLevel;
    this.hrDropdown = res.HR;
  }

  get topicsArray(): UntypedFormArray {
    return this.oneToOneForm.get('oneToOneTopics') as UntypedFormArray;
  }

  topicsArrayControls(index: number): UntypedFormGroup {
    return this.topicsArray.controls[index] as UntypedFormGroup;
  }

  createOneToOneTopicGroup(topic?: IOneToOneTopics) {
    const topicsGroup = this.oneToOneTopicGroup();

    if (topic) {
      topicsGroup.patchValue(topic);
    }
    this.topicsArray.insert(0, topicsGroup);
  }

  oneToOneTopicGroup(): FormGroup {
    return this._fb.group({
      topicComment: [null, Validators.maxLength(400)],
      id: null,
      importanceLevel: null,
      oneToOneId: this.data.value.id,
      satisfactionLevel: null,
      status: null,
      topic: [null, Validators.required],
    });
  }

  saveData() {
    if (this.oneToOneForm.invalid) {
      this.validateAllFormFields(this.oneToOneForm);
      return;
    }
    this.dialogRef.close(this.dataFromOneToOne.value);
  }

  cancelSave() {
    this.dialogRef.close();
  }

  deleteOneToOne() {
    this.hrDataService
      .deleteOneToOne(this.data.value.id)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.dialogRef.close('deleted');
      });
  }

  deleteTopic(i: number): void {
    this.topicsArray.removeAt(i);
  }

  private subscribeOnFormChanges() {
    this.oneToOneForm.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(values => {
        this.dataFromOneToOne.value = values;
      });
  }

  private buildForm(): FormGroup {
    return new UntypedFormGroup({
      date: new UntypedFormControl(
        this.data.value.date || this.todayDateString,
        [Validators.required]
      ),
      employeeId: new UntypedFormControl(this.data.value.employeeId),
      generalComment: new UntypedFormControl({
        disabled: true,
        value: this.data.value.generalComment,
      }),
      hr: new UntypedFormControl(this.data.value.hr, [Validators.required]),
      id: new UntypedFormControl(this.data.value.id),
      riskLevel: new UntypedFormControl(this.data.value.riskLevel, [
        Validators.required,
      ]),
      type: new UntypedFormControl(this.data.value.type, [Validators.required]),
      actionItems: new UntypedFormControl(this.data.value.actionItems, [
        Validators.maxLength(400),
      ]),
      oneToOneTopics: this._fb.array([this.oneToOneTopicGroup()]),
    });
  }

  get todayDateString(): string {
    return Utils.formatDateToString([new Date()]) as string;
  }
}
